import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, imgUrl, bulkDiscountInfo } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, remove, setQty, total, count } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user) {
      setOpen(false);
      toast("Sign in to complete your order");
      nav("/auth", { state: { from: "/" } });
      return;
    }
    setLoading(true);
    try {
      const payload = { items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })), origin_url: window.location.origin };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" />
          <motion.aside data-testid="cart-drawer" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
            className="fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-[#0c0c0c]/95 backdrop-blur-xl border-l border-[#222] flex flex-col">
            <div className="flex items-center justify-between px-6 h-16 border-b border-[#222]">
              <span className="overline text-[#F4F1EC]">Your Order — {items.length}</span>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-[#8A8782] hover:text-[#F4F1EC]"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <p className="text-[#8A8782] text-sm mt-10 text-center">Your order is empty.</p>
              ) : items.map((i) => (
                <div key={i.lookup_key} data-testid={`cart-item-${i.lookup_key}`} className="flex gap-4 py-5 border-b border-[#1a1a1a]">
                  <div className="w-16 h-16 bg-[#121212] overflow-hidden flex-shrink-0">
                    <img src={imgUrl(i.image_url)} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-lg leading-tight">{i.name}</p>
                    <p className="text-[11px] text-[#8A8782] mb-2">{i.size}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-[#222]">
                        <button onClick={() => setQty(i.lookup_key, i.quantity - 1)} className="px-2 py-1 text-[#8A8782] hover:text-[#C7F000]"><Minus size={12} /></button>
                        <span className="px-2 text-xs">{i.quantity}</span>
                        <button onClick={() => setQty(i.lookup_key, i.quantity + 1)} className="px-2 py-1 text-[#8A8782] hover:text-[#C7F000]"><Plus size={12} /></button>
                      </div>
                      <button onClick={() => remove(i.lookup_key)} className="text-[#8A8782] hover:text-[#D94026]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <span className="text-sm">${(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="px-6 py-6 border-t border-[#222]">
              {(() => {
                const { pct, nextTier, unitsToNext } = bulkDiscountInfo(count);
                const shipping = total >= 75 || total === 0 ? 0 : 9.99;
                const discount = +(total * pct / 100).toFixed(2);
                const est = (total - discount + shipping).toFixed(2);
                return (
                  <>
                    <div className="flex justify-between mb-1 text-sm"><span className="text-[#8A8782]">Subtotal</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
                    {pct > 0 && (
                      <div className="flex justify-between mb-1 text-sm text-[#C7F000]">
                        <span>Bulk discount ({pct}% · {count} units)</span>
                        <span data-testid="cart-discount">-${discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between mb-1 text-sm">
                      <span className="text-[#8A8782]">Standard shipping</span>
                      <span data-testid="cart-shipping">{shipping === 0 && total > 0 ? "FREE" : total > 0 ? "$9.99" : "—"}</span>
                    </div>
                    <div className="flex justify-between mb-2 text-sm text-[#F4F1EC]">
                      <span>Estimated total</span>
                      <span data-testid="cart-est-total">${total > 0 ? est : "0.00"}</span>
                    </div>
                    {nextTier && count > 0 && (
                      <p className="text-[11px] text-[#C7F000] mb-2" data-testid="bulk-nudge">Add {unitsToNext} more unit{unitsToNext > 1 ? "s" : ""} to unlock {nextTier}% off.</p>
                    )}
                    {total > 0 && total < 75 && (
                      <p className="text-[11px] text-[#8A8782] mb-1">Free standard shipping over $75.</p>
                    )}
                  </>
                );
              })()}
              <p className="text-[11px] text-[#8A8782] mb-4 mt-1">Faster tiers & tax shown at checkout.</p>
              <button data-testid="checkout-btn" disabled={items.length === 0 || loading} onClick={checkout}
                className="btn-toxic w-full py-4 disabled:opacity-40 disabled:cursor-not-allowed">
                {loading ? "Redirecting…" : "Proceed to Checkout"}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
